import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Lock, ArrowRight, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("A senha precisa ter ao menos 6 caracteres");
      return;
    }
    if (password !== confirm) {
      setError("As senhas não coincidem");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setSuccess(true);
      setTimeout(() => nav("/admin/login"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-alabaster">
        <div data-testid="reset-no-token" className="text-center">
          <p className="font-serif text-2xl text-onyx">Token ausente</p>
          <p className="text-sm text-onyx/60 mt-2">
            <Link to="/admin/forgot-password" className="text-gold hover:underline">Solicitar novo link</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="reset-password-page" className="min-h-screen flex items-center justify-center bg-alabaster px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <p className="overline mb-3">REDEFINIR SENHA</p>
          <h1 className="font-serif text-4xl text-onyx">Nova Senha</h1>
        </div>

        {success ? (
          <div data-testid="reset-success" className="bg-white border border-border p-8 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gold mb-4">
              <Check size={24} className="text-onyx" />
            </div>
            <p className="font-serif text-2xl text-onyx">Senha redefinida!</p>
            <p className="text-sm text-onyx/60 mt-2">Redirecionando para o login…</p>
          </div>
        ) : (
          <form data-testid="reset-password-form" onSubmit={onSubmit} className="bg-white border border-border p-8 space-y-5">
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-onyx/60 mb-2">Nova senha</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-onyx/40" />
                <input
                  data-testid="reset-password-input"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-3 py-3 border border-border bg-alabaster text-onyx focus:outline-none focus:border-gold text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-onyx/60 mb-2">Confirmar nova senha</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-onyx/40" />
                <input
                  data-testid="reset-confirm-input"
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full pl-10 pr-3 py-3 border border-border bg-alabaster text-onyx focus:outline-none focus:border-gold text-sm"
                />
              </div>
            </div>
            {error && <p data-testid="reset-error" className="text-sm text-destructive">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              data-testid="reset-submit"
              className="w-full inline-flex items-center justify-center gap-3 py-3.5 bg-onyx text-alabaster text-xs tracking-[0.25em] uppercase font-semibold hover:bg-gold hover:text-onyx transition-colors disabled:opacity-50"
            >
              {submitting ? "Redefinindo…" : "Redefinir Senha"}
              <ArrowRight size={14} />
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
